import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Rocket, Mail, AlertTriangle, CheckCircle2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

/**
 * Settings → "App is Live!" launch email blast card (Super Admin only).
 *
 * One-click action that resets the password for every active employee to a
 * fresh random value, sends each one a branded welcome email with their
 * login URL + credentials, and stamps `system_settings.app_live_email_sent`
 * so the action is idempotent (a `force` re-send is available).
 */
export const AppLiveEmailCard = () => {
  const { user: currentUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/announcements/app-live/status');
      if (r.data.success) setStatus(r.data.data);
    } catch (e) {
      // 403 means not super-admin → just hide the card by leaving status null
      if (e.response?.status !== 403) {
        toast.error(e.response?.data?.detail || 'Failed to load status');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSend = async () => {
    setSending(true);
    try {
      const force = !!status?.already_sent;
      const r = await api.post(`/admin/announcements/app-live/send?force=${force}`);
      if (r.data.success) {
        setResult(r.data.data);
        setConfirmOpen(false);
        setResultOpen(true);
        fetchStatus();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const openTestDialog = () => {
    setTestEmail(currentUser?.email || '');
    setTestOpen(true);
  };

  const handleSendTest = async () => {
    const target = (testEmail || '').trim();
    if (!target || !target.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setTestSending(true);
    try {
      const r = await api.post('/admin/announcements/app-live/send-test', { email: target });
      if (r.data.success) {
        toast.success(`Preview sent to ${target}. No employee passwords were touched.`);
        setTestOpen(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally { setTestSending(false); }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></CardContent>
      </Card>
    );
  }
  // Super-admin gate failed → 403 returned, status remains null → don't render the card
  if (!status) return null;

  const sent = status.already_sent;
  const details = status.details;

  return (
    <Card data-testid="app-live-email-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Rocket className="h-5 w-5 text-primary" />Launch Email Blast</CardTitle>
        <p className="text-sm text-muted-foreground mt-1.5">
          Send a one-time welcome email to all <strong>{status.active_employee_count} active employees</strong>. Each email will include their login URL, username (official email), and a freshly-generated random password. Use this <strong>only</strong> the moment you're ready to make the app live for your team.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {sent ? (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm" data-testid="app-live-sent-banner">
            <div className="flex items-center gap-2 font-semibold text-emerald-500"><CheckCircle2 className="h-4 w-4" />Launch email already dispatched</div>
            {details && (
              <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                <div>Sent: {new Date(details.sent_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                <div>Delivered: <strong>{details.sent_count}</strong> · Failed: <strong>{details.failed_count}</strong></div>
                <div>By: {details.triggered_by}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm" data-testid="app-live-not-sent-banner">
            <div className="flex items-center gap-2 font-semibold text-amber-500"><AlertTriangle className="h-4 w-4" />Not yet sent</div>
            <p className="mt-1.5 text-xs text-muted-foreground">Click below when you're ready. All active employees' passwords will be reset to fresh random values at that moment.</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setConfirmOpen(true)} className="bg-primary hover:bg-primary/90" data-testid="app-live-send-btn">
            <Mail className="h-4 w-4 mr-2" />{sent ? 'Resend Launch Email…' : 'Send Launch Email…'}
          </Button>
          <Button variant="outline" onClick={openTestDialog} data-testid="app-live-send-test-btn">
            <Send className="h-4 w-4 mr-2" />Send Test Email
          </Button>
        </div>
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="app-live-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" />Confirm Launch Email</DialogTitle>
            <DialogDescription className="pt-2">
              This will:
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm space-y-1.5 list-disc pl-5">
            <li>Reset the password for every active employee ({status.active_employee_count} users) to a freshly-generated unique random value</li>
            <li>Send each one an email at their <strong>official email</strong> with login URL, username, and the new password</li>
            <li>Mark the launch email as sent (subsequent sends require explicit Resend)</li>
          </ul>
          {sent && (
            <div className="text-xs text-amber-500 bg-amber-500/10 p-2 rounded border border-amber-500/30">
              ⚠ You've already sent this once. Re-sending will overwrite each employee's existing password again.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-primary hover:bg-primary/90" data-testid="app-live-confirm-btn">
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Summary */}
      <Dialog open={resultOpen} onOpenChange={setResultOpen}>
        <DialogContent className="max-w-lg" data-testid="app-live-result-dialog">
          <DialogHeader><DialogTitle>Launch Email Sent</DialogTitle></DialogHeader>
          {result && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded border border-emerald-500/30 bg-emerald-500/5"><div className="text-xs text-muted-foreground">Sent</div><div className="text-2xl font-bold text-emerald-500">{result.sent}</div></div>
                <div className="p-3 rounded border border-red-500/30 bg-red-500/5"><div className="text-xs text-muted-foreground">Failed</div><div className="text-2xl font-bold text-red-500">{result.failed}</div></div>
                <div className="p-3 rounded border border-amber-500/30 bg-amber-500/5"><div className="text-xs text-muted-foreground">Skipped</div><div className="text-2xl font-bold text-amber-500">{result.skipped}</div></div>
              </div>
              <div className="max-h-60 overflow-auto border border-border rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-muted/30 sticky top-0">
                    <tr><th className="text-left p-2">Employee</th><th className="text-left p-2">Status</th></tr>
                  </thead>
                  <tbody>
                    {(result.rows || []).map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2">{row.name}</td>
                        <td className="p-2">
                          {row.status === 'sent' ? (
                            <Badge className="bg-emerald-500/10 text-emerald-500">sent</Badge>
                          ) : row.status === 'failed' ? (
                            <span className="text-red-500" title={row.error}>failed</span>
                          ) : (
                            <span className="text-amber-500">{row.status}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setResultOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Test/Preview Email Dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="app-live-test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-primary" />Send Test Email</DialogTitle>
            <DialogDescription className="pt-2">
              Send a preview of the launch email to any address — useful for verifying formatting before triggering the full blast. <strong>No employee passwords are reset</strong>, and the password shown in the email is a clearly-labelled sample.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="app-live-test-email">Recipient email</Label>
            <Input
              id="app-live-test-email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="preview@example.com"
              data-testid="app-live-test-email-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && !testSending) handleSendTest(); }}
            />
            <p className="text-xs text-muted-foreground">Defaults to your own admin email. Change it to send the preview anywhere.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={handleSendTest} disabled={testSending} data-testid="app-live-test-send-btn">
              {testSending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
